/** Formatting that follows the clinic's locale, currency and time zone (from the session). */

export function formatMoney(minor: number | null | undefined, currency: string, locale: string) {
  if (minor === null || minor === undefined) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(minor / 100);
}

/** "1,200.50" typed by a user → 120050 minor units. Returns null when not a number. */
export function parseMoney(input: string): number | null {
  const clean = input.replace(/[^\d.]/g, "");
  if (!clean || !/^\d+(\.\d{0,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}

export const toMoneyInput = (minor: number | null | undefined) => (minor === null || minor === undefined ? "" : String(minor / 100));

export function formatDate(iso: string | null | undefined, locale: string, timeZone?: string) {
  if (!iso) return "—";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric", timeZone: /^\d{4}-\d{2}-\d{2}$/.test(iso) ? "UTC" : timeZone }).format(date);
}

export function formatDateTime(iso: string | null | undefined, locale: string, timeZone: string) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(iso));
}

export function formatTime(iso: string, locale: string, timeZone: string) {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(new Date(iso));
}

export function ageFrom(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(`${dob}T00:00:00Z`);
  const now = new Date();
  let age = now.getUTCFullYear() - d.getUTCFullYear();
  if (now.getUTCMonth() < d.getUTCMonth() || (now.getUTCMonth() === d.getUTCMonth() && now.getUTCDate() < d.getUTCDate())) age--;
  return age;
}

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** "Today" as yyyy-MM-dd in a time zone. */
export function todayIn(timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/** Local date + time in a zone → ISO string with the zone's offset at that moment. */
export function zonedIso(date: string, time: string, timeZone: string): string {
  const guess = new Date(`${date}T${time}:00Z`);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(guess);
  const get = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  const offsetMin = Math.round((asUtc - guess.getTime()) / 60_000);
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  return `${date}T${time}:00${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/* ------------------------------------------------------------------ calendar time-zone math */

const zoneParts = (iso: string, timeZone: string) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return { date: `${get("year")}-${get("month")}-${get("day")}`, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
};

/** Minutes since local midnight of an instant, in a time zone (09:30 → 570). */
export const minutesInZone = (iso: string, timeZone: string) => zoneParts(iso, timeZone).minutes;

/** The local calendar date (yyyy-MM-dd) of an instant, in a time zone. */
export const localDateOf = (iso: string, timeZone: string) => zoneParts(iso, timeZone).date;

/** yyyy-MM-dd plus n calendar days (n may be negative). */
export function addDays(date: string, n: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The Monday on or before a yyyy-MM-dd date (weeks start on Monday, like WEEKDAYS). */
export function startOfWeek(date: string) {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(date, -((dow + 6) % 7));
}

/** 570 → "09:30". */
export const minutesToTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/**
 * How the dashboard greets someone: their first name, after any "Dr" title typed into the name.
 * Doctors (a doctor record in the clinic, or "Dr" in the name) get "Dr." in front: "Dr. Kausthu N Das" → "Dr. Kausthu".
 */
export function greetingName(fullName: string, isDoctor: boolean) {
  const m = /^\s*(?:dr\.?|doctor)(?:\s+|\.)(.*)$/i.exec(fullName);
  const first = (m ? m[1]! : fullName).trim().split(/\s+/)[0] ?? "";
  return (m || isDoctor) && first ? `Dr. ${first}` : first;
}
